import { createRoot } from "react-dom/client";
import { ConfigProvider, theme } from "antd";
import App from "./App";
import "./index.css";
// Initialize i18n
import i18n from "./core/i18n";
// PWA Service Worker
import { register as registerServiceWorker, unregister as unregisterServiceWorker } from "./core/pwa/serviceWorkerRegistration";
import { initDB } from "./core/pwa/offlineStorage";
import { isNativePlatform } from "./core/native";
// Theme
import { ThemeProvider, useTheme } from "./core/hooks/useTheme";
import { BrandingProvider, useBranding } from "./core/hooks/useBranding";
import { BRAND_DEFAULT_COLOR, withAlpha } from "./core/theme/palette";
// Ant Design locales
import enUS from "antd/locale/en_US";
import ruRU from "antd/locale/ru_RU";
// dayjs locales. antd's own locale translates the chrome (buttons, pagination)
// but every month and weekday name on a date picker comes from dayjs, which
// stays English until it is told otherwise — that is why the tuition month
// picker read "August 2026" to an Uzbek admin.
import dayjs from "dayjs";
import "dayjs/locale/ru";
import "dayjs/locale/uz-latn";
import { useState, useEffect, useMemo } from "react";

// Uzbek locale for Ant Design (based on English, with Uzbek pagination strings)
const uzUZ = {
  ...enUS,
  Table: {
    ...enUS.Table,
    filterTitle: 'Filter',
    filterConfirm: 'OK',
    filterReset: 'Tozalash',
    selectAll: 'Hammasini tanlash',
    selectInvert: 'Teskarisini tanlash',
  },
  Pagination: {
    items_per_page: '/ sahifa',
    jump_to: "O'tish",
    jump_to_confirm: 'Tasdiqlash',
    page: 'Sahifa',
    prev_page: 'Oldingi sahifa',
    next_page: 'Keyingi sahifa',
    prev_5: 'Oldingi 5 sahifa',
    next_5: 'Keyingi 5 sahifa',
    prev_3: 'Oldingi 3 sahifa',
    next_3: 'Keyingi 3 sahifa',
  },
  Modal: { ...enUS.Modal, okText: 'OK', cancelText: 'Bekor qilish' },
  Popconfirm: { ...enUS.Popconfirm, okText: 'Ha', cancelText: "Yo'q" },
  Empty: { ...enUS.Empty, description: "Ma'lumot yo'q" },
};

// Initialize offline storage
initDB().catch(console.error);

const ANT_LOCALES = { en: enUS, ru: ruRU, uz: uzUZ };

// dayjs ships `uz` in Cyrillic; the app writes Uzbek in Latin script.
const DAYJS_LOCALES = { en: 'en', ru: 'ru', uz: 'uz-latn' };

// App wrapper with theme-aware ConfigProvider
const ThemedApp = () => {
  const { isDark } = useTheme();
  const { primaryColor } = useBranding();
  const [lang, setLang] = useState(i18n.language || 'uz');

  useEffect(() => {
    const onLangChanged = (lng) => setLang(lng);
    i18n.on('languageChanged', onLangChanged);
    return () => i18n.off('languageChanged', onLangChanged);
  }, []);

  const antLocale = useMemo(() => ANT_LOCALES[lang] || ANT_LOCALES.uz, [lang]);

  // Global, because dayjs formatting happens all over the app outside React —
  // in table renderers, formatters and utils that never see a context.
  useMemo(() => dayjs.locale(DAYJS_LOCALES[lang] || DAYJS_LOCALES.uz), [lang]);

  // Surface/text tokens are the ones that must track the active theme — hard-
  // coding light values here would otherwise override darkAlgorithm and leave
  // antd surfaces white in dark mode. Values mirror the CSS token palette in
  // index.css so custom and antd chrome stay in sync.
  const palette = isDark
    ? {
        colorBgLayout: '#14120f',
        colorBgContainer: '#1e1b17',
        colorBgElevated: '#26221c',
        colorBorder: '#322d26',
        colorBorderSecondary: '#262119',
        colorText: '#f5f1e8',
        colorTextSecondary: '#b5a891',
        colorTextTertiary: '#a2957e',
        colorTextDescription: '#b5a891',
        headerBg: '#1e1b17',
        bodyBg: '#14120f',
        tableHeaderBg: '#242019',
        tableHeaderColor: '#b5a891',
        tableRowHoverBg: '#242019',
        tagDefaultBg: '#2e2921',
        tagDefaultColor: '#cfc4b0',
        boxShadow:
          '0 1px 3px rgba(0, 0, 0, 0.45), 0 1px 2px rgba(0, 0, 0, 0.4)',
        boxShadowSecondary:
          '0 4px 8px -2px rgba(0, 0, 0, 0.5), 0 2px 4px -2px rgba(0, 0, 0, 0.4)',
      }
    : {
        colorBgLayout: '#faf7f1',
        colorBgContainer: '#ffffff',
        colorBgElevated: '#ffffff',
        colorBorder: '#e6dfd2',
        colorBorderSecondary: '#f4efe6',
        colorText: '#14120f',
        colorTextSecondary: '#5c5346',
        colorTextTertiary: '#6c6355',
        colorTextDescription: '#5c5346',
        headerBg: '#ffffff',
        bodyBg: '#faf7f1',
        tableHeaderBg: '#f4efe6',
        tableHeaderColor: '#5c5346',
        tableRowHoverBg: '#f4efe6',
        tagDefaultBg: '#f4efe6',
        tagDefaultColor: '#443d33',
        boxShadow:
          '0 1px 3px rgba(28, 22, 14, 0.07), 0 1px 2px rgba(28, 22, 14, 0.05)',
        boxShadowSecondary:
          '0 4px 8px -2px rgba(28, 22, 14, 0.07), 0 2px 4px -2px rgba(28, 22, 14, 0.05)',
      };

  // Meaning-carrying colours never follow the tenant, and they change between
  // themes because a value legible on warm paper is not legible on warm ink.
  const fn = isDark
    ? { pistachio: '#6fbf73', saffron: '#e0a33a', garnet: '#e06a72' }
    : { pistachio: '#2d7642', saffron: '#946010', garnet: '#b5303c' };

  const brandColor = primaryColor || BRAND_DEFAULT_COLOR;

  return (
    <ConfigProvider
      locale={antLocale}
      theme={{
        algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          // Brand. The centre's own colour when it set one; the product default
          // otherwise. Only primary follows it — success/warning/error carry
          // meaning and must stay legible whatever colour a centre picks, and
          // info is majolica, which is one of the seven fixed functional hues.
          colorPrimary: brandColor,
          colorInfo: isDark ? '#4c9bd6' : '#1d6fa8',
          colorSuccess: fn.pistachio,
          colorWarning: fn.saffron,
          colorError: fn.garnet,

          // Surfaces (theme-aware)
          colorBgLayout: palette.colorBgLayout,
          colorBgContainer: palette.colorBgContainer,
          colorBgElevated: palette.colorBgElevated,
          colorBorder: palette.colorBorder,
          colorBorderSecondary: palette.colorBorderSecondary,

          // Text (theme-aware)
          colorText: palette.colorText,
          colorTextSecondary: palette.colorTextSecondary,
          colorTextTertiary: palette.colorTextTertiary,
          colorTextDescription: palette.colorTextDescription,

          // Typography. Onest for everything antd draws — mono is figures-only
          // now and reaches those surfaces through index.css, not from here.
          // The scale mirrors the --font-size-* ramp so a page header and an
          // antd heading of the same rank are the same size.
          fontFamily:
            "'Onest', 'Segoe UI', system-ui, -apple-system, sans-serif",
          fontSize: 15,
          fontSizeSM: 13,
          fontSizeLG: 17,
          fontSizeHeading1: 30,
          fontSizeHeading2: 24,
          fontSizeHeading3: 20,
          fontSizeHeading4: 17,
          fontSizeHeading5: 15,
          lineHeight: 1.45,
          lineHeightHeading1: 1.2,
          lineHeightHeading2: 1.25,
          lineHeightHeading3: 1.3,
          lineHeightHeading4: 1.35,
          lineHeightHeading5: 1.4,

          // Geometry. Re-judged against Onest rather than inherited from the
          // mono baseline: a 34px control was sized for a 13px face and crowds
          // a 15px one, and the horizontal padding was cut to 14 only because
          // mono ran wide. Both go back to what the new face wants — and 36
          // is what Button/Input/Select/DatePicker were already overriding to.
          borderRadius: 8,
          borderRadiusLG: 10,
          borderRadiusSM: 6,
          controlHeight: 36,
          controlHeightSM: 28,
          controlHeightLG: 44,
          paddingContentHorizontal: 16,

          // Motion / shadows (theme-aware)
          boxShadow: palette.boxShadow,
          boxShadowSecondary: palette.boxShadowSecondary,
          motionDurationMid: '0.2s',
        },
        components: {
          Card: {
            borderRadiusLG: 12,
            paddingLG: 20,
            headerBg: 'transparent',
          },
          Layout: {
            // Sider is intentionally dark in both themes (matches the CSS
            // --color-sidebar-bg); only header/body track the theme.
            siderBg: isDark ? '#100e0c' : '#1b1814',
            headerBg: palette.headerBg,
            headerHeight: 48,
            headerPadding: '0 20px',
            bodyBg: palette.bodyBg,
          },
          Menu: {
            darkItemBg: 'transparent',
            darkSubMenuItemBg: 'transparent',
            // A tint of whatever the accent turned out to be — the selected nav
            // item is one of the four slots the tenant's colour actually owns.
            darkItemSelectedBg: withAlpha(brandColor, 0.28),
            darkItemHoverBg: 'rgba(245, 241, 232, 0.07)',
            itemBorderRadius: 8,
            itemHeight: 40,
            itemMarginInline: 8,
          },
          Button: {
            controlHeight: 36,
            paddingInline: 16,
            fontWeight: 500,
          },
          Table: {
            headerBg: palette.tableHeaderBg,
            headerColor: palette.tableHeaderColor,
            rowHoverBg: palette.tableRowHoverBg,
            cellPaddingBlock: 12,
          },
          Modal: {
            paddingContentHorizontalLG: 24,
            titleFontSize: 16,
          },
          Tag: {
            defaultBg: palette.tagDefaultBg,
            defaultColor: palette.tagDefaultColor,
          },
          Input: {
            controlHeight: 36,
          },
          Select: {
            controlHeight: 36,
          },
          DatePicker: {
            controlHeight: 36,
          },
          Tabs: {
            cardGutter: 4,
            horizontalItemPaddingSM: '8px 12px',
          },
        },
      }}
    >
      <App />
    </ConfigProvider>
  );
};

createRoot(document.getElementById("root")).render(
  <ThemeProvider>
    {/*
      Inside ThemeProvider so the logo variant can follow light/dark, and
      outside ThemedApp so the centre's colour can reach antd's token set —
      white-labelling that stops at the login logo is not white-labelling.
    */}
    <BrandingProvider>
      <ThemedApp />
    </BrandingProvider>
  </ThemeProvider>
);

// The native shell serves its assets from the APK, so a service worker there
// would cache https://localhost for no benefit and can strand users on stale
// code after an app update. Web PWA only.
const pwaEnabled = process.env.REACT_APP_ENABLE_PWA === 'true' && !isNativePlatform();

if (pwaEnabled) {
  // Register service worker only when explicitly enabled.
  registerServiceWorker({
    onSuccess: () => {
      console.log('PWA: Content cached for offline use');
    },
    onUpdate: () => {
      console.log('PWA: New content available, please refresh');
      const updateBanner = document.createElement('div');
      updateBanner.id = 'pwa-update-banner';
      updateBanner.style.cssText = `
        position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%);
        background: var(--color-primary); color: var(--color-text-on-primary);
        padding: 12px 24px; border-radius: 8px;
        box-shadow: 0 4px 12px rgba(28,22,14,0.18); z-index: 99999; display: flex;
        align-items: center; gap: 12px; font-size: var(--font-size-md);
        font-family: var(--font-family-base);
      `;
      const escapeHtml = (str) => String(str).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      })[c]);
      updateBanner.innerHTML = `
        <span>${escapeHtml(i18n.t('pwa.updateAvailable'))}</span>
        <button onclick="window.location.reload()" style="
          background: var(--color-bg-container); color: var(--color-primary); border: none; padding: 6px 16px;
          border-radius: 4px; cursor: pointer; font-weight: 600; font-size: 13px;
        ">${escapeHtml(i18n.t('pwa.refreshNow'))}</button>
        <button onclick="this.parentElement.remove()" style="
          background: transparent; color: white; border: 1px solid rgba(255,255,255,0.4);
          padding: 6px 12px; border-radius: 4px; cursor: pointer; font-size: 13px;
        ">${escapeHtml(i18n.t('pwa.later'))}</button>
      `;
      const existing = document.getElementById('pwa-update-banner');
      if (existing) existing.remove();
      document.body.appendChild(updateBanner);
    },
  });
} else {
  unregisterServiceWorker();
}
